import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";
import { FormType, Locales } from "@simplyk/common";
import { Control, UseFormReturn, useWatch } from "react-hook-form";

import type { StepAdvancedSettingsFormData } from "../../../types";
import { SwapCampaignModal, type SwapCampaignSupportForm } from "../SwapCampaignModal/SwapCampaignModal";

import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ExternalLink } from "@/icons/outlined";

export type DisplayOnOrgPageChangedParams = {
  new_value: "Display" | "Hide";
  replace_existing_campaign: boolean;
};

interface DisplayOnOrgPageBlockProps {
  form: UseFormReturn<StepAdvancedSettingsFormData>;
  control: Control<StepAdvancedSettingsFormData>;
  formId: string;
  isTicketingWithDate: boolean;
  orgPageUrl: string | undefined;
  hasThreeSupportForms: boolean;
  supportForms: SwapCampaignSupportForm[];
  locale: Locales;
  onDisplayOnOrgPageChanged: (params: DisplayOnOrgPageChangedParams) => void;
}

export const DisplayOnOrgPageBlock: FC<DisplayOnOrgPageBlockProps> = ({
  form,
  control,
  formId,
  isTicketingWithDate,
  orgPageUrl,
  hasThreeSupportForms,
  supportForms,
  locale,
  onDisplayOnOrgPageChanged,
}) => {
  const { t } = useTranslate();
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [selectedReplaceForm, setSelectedReplaceForm] = useState<{ id: string; formType: FormType } | null>(null);

  const displayOnOrgPageSupportForm = useWatch({ control, name: "displayOnOrgPageSupportForm" });
  const isCurrentFormInSupportForms = supportForms.some((sf) => sf.id === formId);

  const openSwapModal = () => {
    setSelectedReplaceForm(null);
    setSwapModalOpen(true);
  };

  const reattachCurrentFormToOrgPage = () => {
    form.setValue("displayOnOrgPageSupportForm", true, { shouldDirty: true });
    form.setValue("replaceFormOnOrgPage", null, { shouldDirty: true });
    onDisplayOnOrgPageChanged({ new_value: "Display", replace_existing_campaign: false });
  };

  const handleSwapConfirm = () => {
    if (selectedReplaceForm) {
      form.setValue("displayOnOrgPageSupportForm", true, { shouldDirty: true });
      form.setValue(
        "replaceFormOnOrgPage",
        { formType: selectedReplaceForm.formType, formId: selectedReplaceForm.id },
        { shouldDirty: true }
      );
      // When user had to open the swap modal (3 support forms), we only send the event after they confirm
      onDisplayOnOrgPageChanged({ new_value: "Display", replace_existing_campaign: true });
      setSwapModalOpen(false);
      setSelectedReplaceForm(null);
    }
  };

  const label = (
    <Stack direction="row" alignItems="center" gap={1} flex={1}>
      <DashboardTypography variant="body1">
        {t("dashboard", "step.advancedSettings.displayOnOrgPage")}
      </DashboardTypography>
      <DashboardInformationTooltip
        title={t("dashboard", "step.advancedSettings.displayOnOrgPageTooltip")}
        slotProps={{ icon: { size: "small" } }}
      />
      {orgPageUrl && (
        <a
          href={orgPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("dashboard", "step.advancedSettings.viewOrgPage")}
          style={{ display: "flex", lineHeight: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <DashboardIcon>
            <ExternalLink />
          </DashboardIcon>
        </a>
      )}
    </Stack>
  );

  return (
    <>
      <LiveEditorBlock>
        {isTicketingWithDate ? (
          <LiveEditorToggle
            label={label}
            name="displayOnOrgPage"
            control={control}
            data-test="display-on-org-page-toggle"
            onValueChange={(checked) =>
              onDisplayOnOrgPageChanged({
                new_value: checked ? "Display" : "Hide",
                replace_existing_campaign: false,
              })
            }
          />
        ) : (
          <Box
            onClickCapture={
              hasThreeSupportForms && !displayOnOrgPageSupportForm
                ? (e) => {
                    if (e.target instanceof HTMLElement && e.target.closest("a")) {
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    if (isCurrentFormInSupportForms) {
                      reattachCurrentFormToOrgPage();
                    } else {
                      openSwapModal();
                    }
                  }
                : undefined
            }
            sx={hasThreeSupportForms && !displayOnOrgPageSupportForm ? { cursor: "pointer" } : undefined}
          >
            <LiveEditorToggle
              label={label}
              name="displayOnOrgPageSupportForm"
              control={control}
              data-test="display-on-org-page-toggle"
              onValueChange={(checked) => {
                if (!checked) {
                  form.setValue("replaceFormOnOrgPage", null, { shouldDirty: true });
                  onDisplayOnOrgPageChanged({ new_value: "Hide", replace_existing_campaign: false });
                } else if (!hasThreeSupportForms) {
                  // Toggle ON without modal: event when change is effective
                  onDisplayOnOrgPageChanged({ new_value: "Display", replace_existing_campaign: false });
                }
                // When hasThreeSupportForms: we do not send here; click opens modal and event is sent only in handleSwapConfirm on confirm
              }}
            />
          </Box>
        )}
      </LiveEditorBlock>

      <SwapCampaignModal
        open={swapModalOpen}
        onClose={() => setSwapModalOpen(false)}
        supportForms={supportForms}
        locale={locale}
        selectedReplaceForm={selectedReplaceForm}
        onSelectReplaceForm={setSelectedReplaceForm}
        onConfirm={handleSwapConfirm}
      />
    </>
  );
};
